// Reconfirm sold-out cards using native Liquid in the visitor's current context.
// This repairs stale HTML when Shopify now returns availability; it never uses
// aggregate inventory or assumes that a failed request means a product is stocked.
(() => {
  const cardSelector = '.product-card[data-inventory-product-id]';
  let checked = new WeakSet();
  const pending = new Map();
  let timer;
  let sequence = 0;

  const context = () => JSON.stringify([location.href, Shopify.country, document.documentElement.lang]);
  const productLink = (card) => {
    const href = card.querySelector('a[href*="/products/"]')?.getAttribute('href');
    if (!href) return null;
    const url = new URL(href, location.origin);
    // Search/recommendation tracking parameters may change on a fresh render.
    return [url.origin, url.pathname, url.searchParams.get('variant'), url.searchParams.get('selling_plan')];
  };
  const fingerprint = (card) => JSON.stringify([
    card.dataset.inventoryProductId,
    productLink(card),
    [...card.querySelectorAll('form [name="id"]')].map((input) => input.value)
  ]);

  function correctCard(card, source, originalFingerprint) {
    if (!card.isConnected || fingerprint(card) !== originalFingerprint ||
        !card.querySelector('.f-badge--soldout') || source.dataset.inventoryAvailable !== 'true' ||
        source.querySelector('.f-badge--soldout') ||
        source.dataset.inventoryCountry !== card.dataset.inventoryCountry) return;

    // Preserve forms, cached element references, swatches and carousel position.
    // Validate every direct-purchase control before changing any availability.
    const controls = [];
    for (const form of card.querySelectorAll('form[data-type="add-to-cart-form"]')) {
      const freshForm = [...source.querySelectorAll('form[data-type="add-to-cart-form"]')]
        .find((candidate) => candidate.getAttribute('id') === form.getAttribute('id'));
      const input = form.querySelector('[name="id"]');
      const freshInput = freshForm?.querySelector('[name="id"]');
      const button = form.querySelector('[name="add"]');
      const freshButton = freshForm?.querySelector('[name="add"]');
      if (!input || !freshInput || input.value !== freshInput.value || !button || !freshButton ||
          button.classList.contains('btn--loading') || button.getAttribute('aria-disabled') === 'true') return;
      controls.push({ input, freshInput, button, freshButton });
    }
    for (const { input, freshInput, button, freshButton } of controls) {
      input.disabled = freshInput.disabled;
      button.disabled = freshButton.disabled;
      button.innerHTML = freshButton.innerHTML;
    }
    card.querySelectorAll('.f-badge--soldout').forEach((badge) => badge.remove());
    card.dataset.inventoryAvailable = 'true';
  }

  async function revalidate(section, cards) {
    const capturedContext = context();
    const sectionId = section.id.replace(/^shopify-section-/, '');
    const controller = new AbortController();
    const key = `${capturedContext}:${sectionId}`;
    if (pending.has(key)) return;
    pending.set(key, controller);
    const candidates = cards.map((card) => ({ card, fingerprint: fingerprint(card) }));
    cards.forEach((card) => checked.add(card));
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const url = new URL(location.href);
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('inventory_check', `${Date.now()}-${++sequence}`);
      const response = await fetch(url, {
        credentials: 'same-origin', cache: 'no-store', signal: controller.signal
      });
      if (response.status !== 200 || !response.headers.get('content-type')?.includes('text/html')) return;
      const finalUrl = new URL(response.url);
      if (finalUrl.origin !== url.origin || finalUrl.pathname !== url.pathname) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (controller.signal.aborted || capturedContext !== context() || !section.isConnected) return;
      const freshSection = html.getElementById(section.id);
      if (!freshSection) return;
      for (const candidate of candidates) {
        const sources = [...freshSection.querySelectorAll(cardSelector)].filter((source) =>
          source.dataset.inventoryProductId === candidate.card.dataset.inventoryProductId &&
          fingerprint(source) === candidate.fingerprint);
        // Ambiguous or missing matches leave the existing state intact.
        if (sources.length === 1) correctCard(candidate.card, sources[0], candidate.fingerprint);
      }
    } catch {
      // Keep the original sold-out state on timeout, offline or invalid HTML.
    } finally {
      clearTimeout(timeout);
      pending.delete(key);
      schedule();
    }
  }

  function scan() {
    const sections = new Map();
    for (const card of document.querySelectorAll(cardSelector)) {
      if (checked.has(card) || !card.querySelector('.f-badge--soldout') ||
          card.dataset.inventoryCountry !== Shopify.country) continue;
      const section = card.closest('[id^="shopify-section-"]');
      if (!section) continue;
      const key = `${context()}:${section.id.replace(/^shopify-section-/, '')}`;
      if (pending.has(key)) continue;
      if (!sections.has(section)) sections.set(section, []);
      sections.get(section).push(card);
    }
    // A page may contain many sections. Limit concurrent native renders.
    for (const [section, cards] of sections) {
      if (pending.size >= 2) break;
      void revalidate(section, cards);
    }
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(scan, 150);
  }

  function start() {
    if (Shopify.designMode) return;
    new MutationObserver((records) => {
      if (records.some((record) => [...record.addedNodes].some((node) =>
        node.nodeType === 1 && (node.matches(cardSelector) || node.querySelector(cardSelector))))) schedule();
    }).observe(document.body, { childList: true, subtree: true });
    window.addEventListener('pagehide', () => {
      pending.forEach((controller) => controller.abort());
    });
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        checked = new WeakSet();
        schedule();
      }
    });
    schedule();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
